import React from 'react';
import { ExternalLink, Heart } from 'lucide-react';
import { PersonalLogo } from './PersonalLogo';
import { PetClock } from './PetClock';
import { RainbowRoomFeedback, getOrCreateVisitorId } from './RainbowRoomFeedback';
import { useLanguage } from '../i18n/LanguageContext';

const RAINBOW_ROOM_BASE_URL = 'https://rainbow-room-seven.vercel.app/';

export const Hero = ({ onSubmitRainbowRoomFeedback }) => {
  const { t } = useLanguage();
  // Appended so the Rainbow Room can report back "3 minutes reached" for
  // THIS specific visitor via POST /api/rainbow-room/tour-complete.
  const RAINBOW_ROOM_URL = `${RAINBOW_ROOM_BASE_URL}?v=${getOrCreateVisitorId()}`;

  return (
    <section id="hero" className="py-16 sm:py-24 relative">
      {/* Hero background image - bleeds edge to edge, capped to hero's top visual area only */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-screen h-[780px] sm:h-[850px] lg:h-[900px] overflow-hidden pointer-events-none">
        <img 
          src="https://customer-assets-v7afamib.emergentagent.net/job_30c98ecf-aca2-427b-b045-fc9d06652bf3/artifacts/oa46vpi2_Petechtor%20Hero%20image.webp"
          alt="A girl with her dog and cat watching a sunset – the bond we protect"
          loading="eager"
          className="w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, rgba(2,6,23,0.97) 0%, rgba(2,6,23,0.9) 30%, rgba(2,6,23,0.6) 50%, rgba(2,6,23,0.35) 70%, rgba(2,6,23,1) 100%)' }}
        />
      </div>

      <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] items-center relative z-10">
        {/* Left Content */}
        <div className="space-y-6">
          {/* Logo Hero with Personal Upload */}
          <div className="flex items-center gap-5">
            <PersonalLogo size="large" />
            <div className="hidden sm:block">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-300">
                {t('heroProtectWhileHere')}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">{t('heroHonorForever')}</div>
            </div>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={RAINBOW_ROOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-badge-rainbow-room-link"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-sky-400 to-indigo-400 px-4 py-1.5 text-white text-xs font-bold shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200"
            >
              <Heart className="w-3 h-3" />
              {t('heroBadgeRainbowRoom')}
            </a>
            <span className="rounded-full bg-slate-800/50 border border-slate-700/50 px-4 py-1.5 text-xs text-slate-300">
              {t('heroBadgeNoPetLeft')}
            </span>
          </div>

          {/* Subtitle */}
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            {t('heroSubtitle')}
          </p>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
            <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
              {t('petechtor')}
            </span>
            <br />
            <span className="text-white">{t('heroGuardiansOfPetverse')}</span>
          </h1>

          {/* Description */}
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
            {t('heroDescriptionPre')} <span className="text-white font-semibold">{t('heroDescriptionBold')}</span> {t('heroDescriptionPost')}
          </p>

          {/* Rainbow Room Beta Invite */}
          <div className="p-6 rounded-2xl bg-gradient-to-br from-sky-500/10 via-indigo-500/10 to-slate-900/40 border border-sky-400/20">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-900/60 border border-sky-400/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-sky-300 mb-3">
              🌈 {t('heroBetaInviteBadge')}
            </span>
            <h3 className="text-lg font-bold text-white mb-2">{t('heroBetaInviteTitle')}</h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              {t('heroBetaInviteText')}
            </p>
            <p className="text-sm text-slate-300 leading-relaxed mt-2">
              {t('heroBetaInviteText2')}
            </p>
            <p className="text-sm text-slate-300 leading-relaxed mt-2">
              {t('heroBetaInviteText3')}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center gap-4">
            <a
              href={RAINBOW_ROOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-enter-rainbow-room-beta-btn"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold bg-gradient-to-r from-sky-400 via-indigo-400 to-violet-400 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
            >
              <ExternalLink className="w-4 h-4" />
              {t('heroEnterBeta')}
            </a>
            <button
              onClick={() => {
                const joinSection = document.getElementById('waitlist');
                if (joinSection) joinSection.scrollIntoView({ behavior: 'smooth' });
              }}
              data-testid="hero-join-guardians-btn"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold bg-slate-800/70 border border-slate-700 text-slate-200 hover:bg-slate-800 transition-colors duration-300"
            >
              {t('heroJoinGuardiansBtn')}
            </button>
          </div>

          <RainbowRoomFeedback onSubmit={onSubmitRainbowRoomFeedback} />

          <p className="text-xs text-slate-400">
            {t('heroPrivateBetaNote')}
          </p>

          {/* Protection Reciprocity - Emotional Core */}
          <div className="my-6 p-6 rounded-2xl bg-slate-900/50 border border-fuchsia-500/20">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-1">
                <h3 className="text-lg font-bold bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent mb-3">
                  {t('heroWhyWeProtect')}
                </h3>
                <p className="text-base text-slate-300 leading-relaxed italic">
                  {t('heroWhyWeProtectText')}
                </p>
              </div>
              <div className="w-full md:w-64 flex-shrink-0">
                <img 
                  src="https://images.unsplash.com/photo-1529343097104-386dca989049?w=800&h=1000&fit=crop&q=90"
                  alt="Pet offering comfort and unconditional protection to their person"
                  loading="lazy"
                  width="400"
                  height="500"
                  className="w-full rounded-xl shadow-lg border border-slate-700/50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right - Rainbow Room Focus Panel */}
        <div className="space-y-6">
          <div className="text-center sm:text-right" data-testid="hero-protect-tagline">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-slate-200 bg-slate-900/70 border border-slate-700/50 rounded-full px-4 py-2">
              {t('heroProtectWhileHere')} · {t('heroHonorForever')}
            </span>
          </div>
          <PetClock />

          <aside className="relative overflow-hidden rounded-2xl border border-sky-400/20 bg-slate-900/60 p-6 shadow-2xl">
          <div className="space-y-4">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-sky-300">🌈 {t('heroAsideBadge')}</div>
            <div className="text-lg font-bold text-white">{t('heroAsideTitle')}</div>
            <p className="text-sm text-slate-300 leading-relaxed">
              {t('heroAsideText')}
            </p>

            <a
              href={RAINBOW_ROOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-aside-enter-rainbow-room-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-sky-300 hover:text-sky-200 transition-colors"
            >
              {t('heroAsideEnterBeta')}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>

            <div className="flex flex-wrap gap-2 pt-2">
              <span className="rounded-full px-3 py-1 text-[11px] bg-slate-800/50 border border-slate-700/50 text-slate-300">
                {t('heroTagMemorial')}
              </span>
              <span className="rounded-full px-3 py-1 text-[11px] bg-slate-800/50 border border-slate-700/50 text-slate-300">
                {t('heroTagCommunity')}
              </span>
              <span className="rounded-full px-3 py-1 text-[11px] bg-slate-800/50 border border-slate-700/50 text-slate-300">
                {t('heroTagFeedback')}
              </span>
            </div>

            <div className="pt-3 border-t border-slate-700/50">
              <p className="text-[11px] text-slate-500 leading-relaxed">
                {t('heroAsideFooterNote')}
              </p>
            </div>
          </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Hero;
